import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem
} from '@mui/material';

const departments = [
  'IT Operations',
  'Development',
  'DevOps',
  'IT Support',
  'Cloud Infrastructure',
  'Security',
  'QA Testing',
  'Product Management',
  'Data Analytics',
  'Other'
];

const statuses = ['Active', 'Pending Onboarding', 'Offboarded'];

export default function EmployeeDialog({ open, employee, onClose, onSave }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    department: '',
    jobTitle: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    status: 'Pending Onboarding',
    phone: '',
    location: '',
    notes: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (employee) {
      setFormData({
        name: employee.name || '',
        email: employee.email || '',
        department: employee.department || '',
        jobTitle: employee.jobTitle || '',
        startDate: employee.startDate ? new Date(employee.startDate).toISOString().split('T')[0] : '',
        endDate: employee.endDate ? new Date(employee.endDate).toISOString().split('T')[0] : '',
        status: employee.status || 'Pending Onboarding',
        phone: employee.phone || '',
        location: employee.location || '',
        notes: employee.notes || ''
      });
    } else {
      // Reset form for new employee
      setFormData({
        name: '',
        email: '',
        department: '',
        jobTitle: '',
        startDate: new Date().toISOString().split('T')[0],
        endDate: '',
        status: 'Pending Onboarding',
        phone: '',
        location: '',
        notes: ''
      });
    }
    setErrors({});
  }, [employee, open]);

  const handleChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.department.trim()) {
      newErrors.department = 'Department is required';
    }

    if (!formData.jobTitle.trim()) {
      newErrors.jobTitle = 'Job title is required';
    }

    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      // Clean up empty fields
      const dataToSave = { ...formData };
      if (!dataToSave.endDate) delete dataToSave.endDate;
      if (!dataToSave.phone) delete dataToSave.phone;
      if (!dataToSave.location) delete dataToSave.location;
      if (!dataToSave.notes) delete dataToSave.notes;

      onSave(dataToSave);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{employee ? 'Edit Employee' : 'Add New Employee'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Name *"
              value={formData.name}
              onChange={handleChange('name')}
              error={!!errors.name}
              helperText={errors.name}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email *"
              type="email"
              value={formData.email}
              onChange={handleChange('email')}
              error={!!errors.email}
              helperText={errors.email}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Department *"
              value={formData.department}
              onChange={handleChange('department')}
              error={!!errors.department}
              helperText={errors.department}
            >
              {departments.map((dept) => (
                <MenuItem key={dept} value={dept}>
                  {dept}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Job Title *"
              value={formData.jobTitle}
              onChange={handleChange('jobTitle')}
              error={!!errors.jobTitle}
              helperText={errors.jobTitle}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Start Date *"
              type="date"
              value={formData.startDate}
              onChange={handleChange('startDate')}
              error={!!errors.startDate}
              helperText={errors.startDate}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={formData.endDate}
              onChange={handleChange('endDate')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              select
              label="Status *"
              value={formData.status}
              onChange={handleChange('status')}
            >
              {statuses.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Phone"
              value={formData.phone}
              onChange={handleChange('phone')}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Location"
              value={formData.location}
              onChange={handleChange('location')}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes"
              multiline
              rows={3}
              value={formData.notes}
              onChange={handleChange('notes')}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          {employee ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
